/**
 * Floating contact button — a single brand-mark button pinned to the bottom-right
 * of every storefront page. Tapping it opens Instagram and WhatsApp above it;
 * tapping again (or Escape, or anywhere outside) closes them.
 */
import React, { useEffect, useRef, useState } from 'react';
import './SocialRail.css';
import { INSTAGRAM_URL, WHATSAPP_CHAT_URL, CONTACT_EMAIL } from '../data/contact';

/* Brand glyphs are inlined so they render instantly and stay crisp at any size. */
const InstagramGlyph = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16m0-2.16C8.74 0 8.33.01 7.05.07c-1.28.06-2.15.26-2.91.56-.79.31-1.46.72-2.13 1.38C1.35 2.68.94 3.35.63 4.14c-.3.76-.5 1.63-.56 2.91C.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.28.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.67.66 1.34 1.07 2.13 1.38.76.3 1.63.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.28-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.38.66-.67 1.07-1.34 1.38-2.13.3-.76.5-1.63.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.28-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.38-2.13C21.32 1.35 20.65.94 19.86.63c-.76-.3-1.63-.5-2.91-.56C15.67.01 15.26 0 12 0z"
    />
    <path
      fill="currentColor"
      d="M12 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zm0 10.16A4 4 0 1 1 16 12a4 4 0 0 1-4 4z"
    />
    <circle fill="currentColor" cx="18.41" cy="5.59" r="1.44" />
  </svg>
);

const WhatsAppGlyph = () => (
  <svg viewBox="0 0 24 24" width="21" height="21" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35z"
    />
    <path
      fill="currentColor"
      d="M12.04 2A9.94 9.94 0 0 0 2.1 11.94a9.83 9.83 0 0 0 1.35 4.98L2 22l5.23-1.37a9.94 9.94 0 0 0 4.81 1.23h.01A9.94 9.94 0 0 0 22 11.92 9.94 9.94 0 0 0 12.04 2zm5.8 15.74a8.24 8.24 0 0 1-5.8 2.4h-.01a8.26 8.26 0 0 1-4.2-1.15l-.3-.18-3.11.82.83-3.03-.2-.31a8.24 8.24 0 0 1-1.26-4.39 8.26 8.26 0 0 1 14.1-5.83 8.2 8.2 0 0 1 2.43 5.85 8.24 8.24 0 0 1-2.48 5.82z"
    />
  </svg>
);

const EmailGlyph = () => (
  <svg viewBox="0 0 24 24" width="21" height="21" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 2v.4l-8 5-8-5V6h16zm0 12H4V8.7l7.47 4.67a1 1 0 0 0 1.06 0L20 8.7V18z"
    />
  </svg>
);

function SocialRail() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Mouse users get hover-to-open; touch users tap. Checked once, because a tap
  // on a phone also fires a synthetic mouseenter, which would open the menu and
  // then leave it stuck open.
  const canHover = useRef(
    typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );

  // Close on Escape or a click anywhere else on the page.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  return (
    <div
      className={`socialRail ${open ? 'socialRail--open' : ''}`}
      ref={rootRef}
      onMouseEnter={() => { if (canHover.current) setOpen(true); }}
      onMouseLeave={() => { if (canHover.current) setOpen(false); }}
    >
      <div className="socialRailLinks" id="socialRailLinks" aria-hidden={!open}>
        <a
          className="socialRailBtn socialRailBtn--email"
          href={`mailto:${CONTACT_EMAIL}`}
          aria-label={`Email us at ${CONTACT_EMAIL}`}
          tabIndex={open ? 0 : -1}
          onClick={() => setOpen(false)}
        >
          <EmailGlyph />
          <span className="socialRailLabel">Email us</span>
        </a>

        <a
          className="socialRailBtn socialRailBtn--instagram"
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Elaichi & Love on Instagram"
          tabIndex={open ? 0 : -1}
          onClick={() => setOpen(false)}
        >
          <InstagramGlyph />
          <span className="socialRailLabel">Instagram</span>
        </a>

        <a
          className="socialRailBtn socialRailBtn--whatsapp"
          href={WHATSAPP_CHAT_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat with us on WhatsApp"
          tabIndex={open ? 0 : -1}
          onClick={() => setOpen(false)}
        >
          <WhatsAppGlyph />
          <span className="socialRailLabel">WhatsApp</span>
        </a>
      </div>

      <button
        type="button"
        className="socialRailToggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="socialRailLinks"
        aria-label={open ? 'Close contact options' : 'Contact Elaichi & Love'}
      >
        <img src="/brandmark.png" alt="" />
      </button>
    </div>
  );
}

export default SocialRail;
